/**
 * Created by Jerker Nord on 2023-09-22.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {LonerGroundScroller} from "./LonerGroundScroller";
import {LonerSkyScroller} from "./LonerSkyScroller";
import {Loner} from "../../../Loner";
import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {LonerSound} from "../../../sound/LonerSound";
import {LonerTickTickBoomEndCredits} from "./endcredits/LonerTickTickBoomEndCredits";
import {LonerSelectContainer} from "./select/LonerSelectContainer";
import {LonerCar} from "./LonerCar";
import {MAIN_FG_ANIMATION_NAMES} from "../../general/LonerForegroundView";

export class LonerTickTickBoomView extends PIXI.Container implements StageLayer {

    private static GAME_CONTAINER_POSITION:PIXI.Point = new PIXI.Point(-26, 76);
    private static SELECT_CONTAINER_POSITION:PIXI.Point = new PIXI.Point(0, -30);
    private static GROUND_POSITION:PIXI.Point = new PIXI.Point(34, 81);
    private static CAR_START_POSITION:PIXI.Point = new PIXI.Point(100, 390);
    private static SKY_POSITION:PIXI.Point = new PIXI.Point(0, 30);
    private static CAR_END_POSITION_DELAY:number = 4.0;
    private static CAR_END_POSITION:PIXI.Point = new PIXI.Point(476, 345);
    private static END_CREDITS_POSITION:PIXI.Point = new PIXI.Point(230, 200);
    public static SKIP_TIMESCALE:number = 3;

    private _creditBackground:PIXI.Sprite;
    private _gameBackground:PIXI.Sprite;
    private _car:LonerCar;
    private _groundScroller:LonerGroundScroller;
    private _skyScroller:LonerSkyScroller;
    private _gameContainer:PIXI.Container;
    private _onSelectedCallback:Function;
    private _onResultDoneCallback:Function;
    private _selection:number;
    private _moveCarToEndPositionTL:gsap.core.Timeline;
    private _explodeCarTL:gsap.core.Timeline;
    private _explodeCarMaxWinTL:gsap.core.Timeline;
    private _readyToExplode:boolean = false;
    private _serverResult:boolean = false;
    private _endCredits:LonerTickTickBoomEndCredits;
    private _selectContainer:LonerSelectContainer;
    private _showCreditsBackgroundTL:gsap.core.Timeline;
    private _skipActivated:boolean;
    private _showWinRangeTL:gsap.core.Timeline;

    constructor() {
        super();
        this.name = "LonerTickTickBoomView";
        this.init();
    }

    private init():void {
        this._gameContainer = new PIXI.Container();

        this._gameBackground = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._gameBackground.tint = 0x94bfdb;
        this._gameBackground.width = 800;
        this._gameBackground.height = 800;
        this._gameBackground.x = -10;
        this._gameBackground.y = -10;
        this._gameContainer.addChild(this._gameBackground);
        this._gameContainer.position = LonerTickTickBoomView.GAME_CONTAINER_POSITION;

        this._skyScroller = new LonerSkyScroller();
        this._skyScroller.position = LonerTickTickBoomView.SKY_POSITION;
        this._gameContainer.addChild(this._skyScroller);

        this._groundScroller = new LonerGroundScroller();
        this._groundScroller.position = LonerTickTickBoomView.GROUND_POSITION;
        this._gameContainer.addChild(this._groundScroller);

        this._car = new LonerCar();
        this._car.position = LonerTickTickBoomView.CAR_START_POSITION;
        this._gameContainer.addChild(this._car);

        this._selectContainer = new LonerSelectContainer((selection:number) => this.onSelected(selection));
        this._selectContainer.position = LonerTickTickBoomView.SELECT_CONTAINER_POSITION;
        this._selectContainer.visible = false;
        this._gameContainer.addChild(this._selectContainer);

        this._creditBackground = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._creditBackground.tint = 0x000000;
        this._creditBackground.alpha = 0;
        this._creditBackground.width = 1000;
        this._creditBackground.height = 1000;
        this._gameContainer.addChild(this._creditBackground);

        this._gameContainer.alpha = 0;
        this._gameContainer.visible = false;
        this.addChild(this._gameContainer);

        this._endCredits = new LonerTickTickBoomEndCredits();
        this._endCredits.position = LonerTickTickBoomView.END_CREDITS_POSITION;
        this._gameContainer.addChild(this._endCredits);

        this._moveCarToEndPositionTL = gsap.timeline({
            paused : true,
            onComplete : () => this.onCarToEndAnimationComplete()
        });
        this._moveCarToEndPositionTL.add(gsap.timeline({delay : LonerTickTickBoomView.CAR_END_POSITION_DELAY}));
        this._moveCarToEndPositionTL.add(gsap.to(this._car.position, {
            ease : "power1.in",
            duration : 2.5,
            x : (LonerTickTickBoomView.CAR_END_POSITION.x - 50)
        }));
        this._moveCarToEndPositionTL.add([
            gsap.to(this._car.position, {
                ease : "power3.out",
                duration : 1.0,
                x : LonerTickTickBoomView.CAR_END_POSITION.x
            }),
            () => this._skyScroller.slowScroll()
        ]);
        this._moveCarToEndPositionTL.add(this._car.getRandomBreakdownTL(), "-=2");

        this._explodeCarTL = gsap.timeline({paused : true});
        this._explodeCarTL.add([this._car.getExplosionTL().restart(), () => {
            Loner.shake(0.5, 100, 4);
        }]);
        this._explodeCarTL.add(() => this._groundScroller.resultContainer.playFireAnimation());
        this._explodeCarTL.add(() => this._onResultDoneCallback());

        this._explodeCarMaxWinTL = gsap.timeline({paused : true});
        this._explodeCarMaxWinTL.add([this._car.getMaxWinExplosionTL().restart(), () => {
            Loner.shake(1, 200, 8);
        }]);
        this._explodeCarMaxWinTL.add(() => this._groundScroller.resultContainer.playFireAnimation());
        this._explodeCarMaxWinTL.add(() => this._onResultDoneCallback());

        this._showCreditsBackgroundTL = gsap.timeline({paused : true});
        this._showCreditsBackgroundTL.add(gsap.to(this._creditBackground, {duration : 0.1, alpha : 0.7}));

        this.onResize();
    }

    public getShowCreditsBackgroundTL():gsap.core.Timeline {
        return this._showCreditsBackgroundTL.restart();
    }

    public startCredits():gsap.core.Timeline {
        const startCreditsTL:gsap.core.Timeline = gsap.timeline();
        startCreditsTL.add([
            () => this._endCredits.startEndCredits()
        ]);
        return startCreditsTL;
    }

    public start(onSelectedCallback:Function):void {
        this._onSelectedCallback = onSelectedCallback;
        this.startGame();
    }

    private startGame():void {
        this._skyScroller.startScroll();
        this._gameContainer.visible = true;
        this._gameContainer.alpha = 1;
        const startGameTL:gsap.core.Timeline = gsap.timeline();
        startGameTL.add(() => {
            this._car.startCar();
            this._car.getCarExhaustAnimationTL();
        });
        startGameTL.add(gsap.timeline({delay : 0.5}));
        startGameTL.add(() => {
            this._groundScroller.startScroll();
        });
        startGameTL.add(gsap.timeline({delay : 0.4}));
        startGameTL.add(() => {
            this._selectContainer.visible = true;
        });
        startGameTL.add(() => this._selectContainer.startAllAttentions());
    }

    private onSelected(selection:number):void {
        Loner.soundPlayer.playSound(LonerSound.TTB_SELECT);
        Loner.soundPlayer.playSound(LonerSound.TTB_CAR_DRIVING);
        Loner.soundPlayer.stopSound(LonerSound.TTB_INTRO_MUSIC);
        Loner.soundPlayer.playSound(LonerSound.TTB_MUSIC, 1, 10000);
        this._selection = selection;
        SlotGame.apiPlugin.betHandler.pickAndClickBet(selection, false);
        Loner.foregroundView.playTTBArmsAnimation(MAIN_FG_ANIMATION_NAMES.TTB_ARMS_PLAYING, true);
        this._onSelectedCallback();
    }

    private stopCar():void {
        this._groundScroller.stopScroll(this._selection);
    }

    public activateSkip():void {
        this._skipActivated = true;
        if(this._moveCarToEndPositionTL.isActive()) {
            this._moveCarToEndPositionTL.timeScale(LonerTickTickBoomView.SKIP_TIMESCALE);
        }
        if(this._showWinRangeTL && this._showWinRangeTL.isActive()) {
            this._showWinRangeTL.timeScale(LonerTickTickBoomView.SKIP_TIMESCALE);
        }
        this._groundScroller.activateSkip();
    }

    public doStopCar():void {
        let timeScale:number = 1;
        if(this._skipActivated == true) {
            timeScale = LonerTickTickBoomView.SKIP_TIMESCALE;
        }
        this._moveCarToEndPositionTL.restart().timeScale(timeScale);
        this._showWinRangeTL = this._groundScroller.resultContainer.getShowWinRangeTL().timeScale(timeScale);
    }

    private onCarToEndAnimationComplete():void {
        this._readyToExplode = true;
        this.checkExplodeCar();
    }

    private checkExplodeCar():void {
        if(this._readyToExplode == true && this._serverResult == true) {
            Loner.foregroundView.setTTBArmsTimescale(0);
            this._serverResult = false;
            this._readyToExplode = false;
            if(Loner.model.brokeBank == true) {
                this._explodeCarMaxWinTL.restart();
            } else {
                this._explodeCarTL.restart();
            }
        }
    }

    //Called from result runner. We have a server reply with the result
    public showResult(onResultDone:Function):void {
        this._onResultDoneCallback = onResultDone;
        this._serverResult = true;
        this.stopCar();
    }

    public getHideViewTL():gsap.core.Timeline {
        const hideTL:gsap.core.Timeline = gsap.timeline();
        hideTL.add(gsap.to(this._gameContainer, {duration : 1.0, alpha : 0}));
        hideTL.add(() => {
            this._gameContainer.visible = false
        });
        return hideTL;
    }

    public onResize():void {
        if(this._selectContainer) {
            this._selectContainer.onResize();
        }
    }

    public reset():void {
        this._skipActivated = false;
        this._selectContainer.reset();
        this._selectContainer.visible = false;
        this._car.position = LonerTickTickBoomView.CAR_START_POSITION;
        this._car.reset();
        this._groundScroller.reset();
        this._groundScroller.resultContainer.reset();
        this._endCredits.reset();
        this._creditBackground.alpha = 0;
    }
}